import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/ui/Icon';
import type { Suite, PinnedItem, AlertItem } from '../data/types/platform';

const CARBONATE_URL = 'https://carbonateai.com/';
const CARBONATE_LOGO = '/assets/carbonate-wordmark.svg';

export type PrimarySelection = 'hub' | 'admin' | string;

export interface PrimaryNavProps {
  suites: Suite[];
  pinned: PinnedItem[];
  alerts: AlertItem[];
  activeSelection: PrimarySelection;
  /** When false, the admin entry + the multi-system Hub link are hidden.
   *  Defaults to true so existing call sites stay unchanged. */
  adminVisible?: boolean;
  open: boolean;
  toggle: () => void;
}

export function PrimaryNav({
  suites,
  pinned,
  alerts,
  activeSelection,
  adminVisible = true,
  open,
  toggle,
}: PrimaryNavProps) {
  const navigate = useNavigate();

  return (
    <aside className="sidenav sn-primary">
      <div className="sn-head">
        <span className="sn-title">ניווט</span>
        <button
          className="sn-toggle"
          onClick={toggle}
          title={open ? 'כווץ' : 'פתח'}
          aria-label="פתח או כווץ תפריט ראשי"
          type="button"
        >
          <Icon name="panel-right-close" size={16} />
        </button>
      </div>

      <div className="sn-body">
        {adminVisible && (
          <button
            className={'sn-item' + (activeSelection === 'hub' ? ' active' : '')}
            onClick={() => navigate('/')}
            type="button"
          >
            <span className="sn-ic"><Icon name="layout-dashboard" size={18} /></span>
            <span className="sn-label">דשבורד</span>
            <span className="sn-tip">דשבורד</span>
          </button>
        )}

        <div className="sn-section">מערכות</div>

        {suites.map((s) => {
          const isActive = activeSelection === s.id;
          const hasAnomaly = s.modules.some((m) => m.health === 'anomaly');
          const hasGap = s.modules.some((m) => m.health === 'gap');
          return (
            <button
              key={s.id}
              className={'sn-item' + (isActive ? ' active' : '')}
              onClick={() => navigate(`/system/${s.id}`)}
              type="button"
            >
              <span className="sn-ic"><Icon name={s.icon} size={18} /></span>
              <span className="sn-label">{s.he}</span>
              {!s.href ? (
                <span className="sn-tag">בפיתוח</span>
              ) : hasAnomaly ? (
                <span className="sn-status alert" title="אנומליה פעילה" />
              ) : hasGap ? (
                <span className="sn-status warn" title="פער נתונים" />
              ) : null}
              <span className="sn-tip">{s.he}</span>
            </button>
          );
        })}

        {adminVisible && (
          <>
            <div className="sn-divider" />
            <div className="sn-section">כללי</div>
            <button
              className={'sn-item' + (activeSelection === 'admin' ? ' active' : '')}
              onClick={() => navigate('/admin')}
              type="button"
            >
              <span className="sn-ic"><Icon name="shield" size={18} /></span>
              <span className="sn-label">ניהול מערכת</span>
              <span className="sn-tip">ניהול מערכת</span>
            </button>
            <button className="sn-item" onClick={() => navigate('/')} type="button">
              <span className="sn-ic"><Icon name="folder-open" size={18} /></span>
              <span className="sn-label">סביבות עבודה</span>
              <span className="sn-count">5</span>
              <span className="sn-tip">סביבות עבודה</span>
            </button>
            <button className="sn-item" onClick={() => navigate('/')} type="button">
              <span className="sn-ic"><Icon name="pin" size={18} /></span>
              <span className="sn-label">תיוקים</span>
              <span className="sn-count">{pinned.length}</span>
              <span className="sn-tip">תיוקים</span>
            </button>
            <button className="sn-item" onClick={() => navigate('/')} type="button">
              <span className="sn-ic"><Icon name="bell" size={18} /></span>
              <span className="sn-label">התראות</span>
              <span className="sn-count">{alerts.length}</span>
              <span className="sn-tip">התראות</span>
            </button>
          </>
        )}
      </div>

      <div className="sn-foot">
        <div className="sn-foot-meta">
          <span>גרסה 0.4</span>
          <span>תמיכה</span>
        </div>
        <div className="sn-powered">
          <span className="sn-powered-label">מופעל ע״י</span>
          <a
            className="sn-carbonate"
            href={CARBONATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            title="Carbonate AI"
          >
            <img src={CARBONATE_LOGO} alt="Carbonate AI" />
          </a>
        </div>
      </div>
    </aside>
  );
}
